/**
 * Shared "at-a-glance" metric card for the redesigned dashboard (spec
 * section 1: "All cards must have identical size. All values should be
 * prominently displayed. Display the interpretation below each value.
 * Example: OI Score / 5.2 / Neutral.") and section 8 (equal height/
 * padding/radius/shadow across all dashboard cards).
 *
 * Uses `variant="outlined"` (no shadow) — the convention already used by
 * every other card on this page — so radius/shadow stay identical without
 * introducing a new visual style. `height: "100%"` + a shared `minHeight`
 * is what actually makes a row of these cards line up regardless of how
 * long each label/interpretation string is.
 *
 * Purely presentational: renders whatever `value`/`interpretation` it's
 * given. It does not fetch or compute anything itself — see
 * `MarketSummaryStrip.tsx` for exactly which already-existing field feeds
 * each card.
 */
import type { ReactNode } from "react";
import { Box, Card, CardContent, Typography } from "@mui/material";

export function SummaryMetricCard({
  icon,
  label,
  value,
  valueVariant = "h4",
  interpretation,
  accentColor,
}: {
  icon?: ReactNode;
  label: string;
  value: ReactNode;
  valueVariant?: "h4" | "h5" | "h6";
  interpretation?: ReactNode;
  accentColor?: string;
}) {
  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        minHeight: 132,
        borderTop: accentColor ? `3px solid ${accentColor}` : undefined,
      }}
    >
      <CardContent
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Box display="flex" alignItems="center" gap={0.75}>
          {icon}
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
          >
            {label}
          </Typography>
        </Box>
        <Typography variant={valueVariant} fontWeight={800} sx={{ mt: 0.5, lineHeight: 1.15 }}>
          {value}
        </Typography>
        {interpretation !== undefined && interpretation !== null && (
          <Typography
            variant="body2"
            fontWeight={700}
            sx={{ color: accentColor ?? "text.secondary", mt: 0.5 }}
          >
            {interpretation}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default SummaryMetricCard;
